// Moving slides around a deck, as list surgery.
//
// The outline drawer can move one slide or a whole picked-out set, from one
// section or several, to a place described as "this slide, on this side of it".
// All of that is arithmetic on arrays, and it is the part that goes wrong, so
// it lives here rather than inside the component that owns the deck.
//
// What it deliberately does not know: how a section stores its slides (the
// caller splits and flattens), or what a slide id looks like (the caller mints
// them). It is handed lists and hands lists back.

/**
 * The index to insert at, once the moved slides have been taken out.
 *
 * The drop is described against the list as the author sees it — "before the
 * slide at index 3" — but by the time the moved slides are removed, index 3 is
 * a different slide, or past the end. What survives the removal is how many of
 * the slides ahead of the drop point are staying put, and that count *is* the
 * insertion point. It also answers the two awkward cases without a special
 * case: dropping a selection onto one of its own members, and dropping a slide
 * into the gap it is about to leave behind.
 */
export function insertionIndexFor(destination, { toIndex = 0, position = 'before', isMoving }) {
  const list = Array.isArray(destination) ? destination : [];
  const cut = Math.max(0, Math.min(position === 'after' ? toIndex + 1 : toIndex, list.length));
  let staying = 0;
  for (let i = 0; i < cut; i += 1) {
    if (!isMoving(list[i])) staying += 1;
  }
  return staying;
}

/**
 * Nudge a set of picked items one place up or down a list.
 *
 * Each picked item moves by one, and the ones already pressed against the end
 * they are heading for stay where they are and hold the rest behind them. That
 * is what keeps a run of picked slides moving as a block, and what stops a
 * scattered selection from piling up at the top: the items keep their order
 * relative to each other and to everything they pass.
 *
 * Returns a new list. An unchanged list means the selection has nowhere left
 * to go, which is what the buttons are disabled on.
 */
export function shiftSelection(order = [], selectedIds = [], offset = 0) {
  const list = [...order];
  const picked = new Set(selectedIds);
  if (!picked.size || !offset) return list;

  if (offset < 0) {
    // `wall` is the first place still free at the top. A picked item sitting
    // on it cannot move, and the wall grows to cover it.
    let wall = 0;
    for (let i = 0; i < list.length; i += 1) {
      if (!picked.has(list[i])) continue;
      if (i === wall) {
        wall = i + 1;
        continue;
      }
      [list[i - 1], list[i]] = [list[i], list[i - 1]];
    }
    return list;
  }

  let wall = list.length - 1;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (!picked.has(list[i])) continue;
    if (i === wall) {
      wall = i - 1;
      continue;
    }
    [list[i + 1], list[i]] = [list[i], list[i + 1]];
  }
  return list;
}

const isEmptyPlaceholder = (slides) => slides.length === 1
  && Array.isArray(slides[0]?.blocks)
  && slides[0].blocks.length === 0;

/**
 * Plan a move of one or more slides.
 *
 * `slidesBySection` maps a section id to its slides, in order; the slide
 * objects are treated as opaque apart from `slideId` and `blocks`. `refs` name
 * the slides to move — by id, falling back to position — and may name slides in
 * several sections; they arrive at the destination together, in deck order.
 *
 * `mintSlideId(sectionId)` is asked for an id whenever a slide changes section,
 * and for the blank a section emptied by the move is left showing. Ids never
 * change within a section: nothing is regenerated there, and keeping them means
 * saved names, durations and undo still line up.
 *
 * Returns the sections it touched, alongside what the caller has to act on —
 * the renamed ids to carry per-slide settings across, and the sections the move
 * emptied.
 */
export function planSlideMove({
  slidesBySection = {},
  sectionOrder = [],
  refs = [],
  toSectionId,
  toIndex = 0,
  position = 'before',
  mintSlideId = () => ''
} = {}) {
  const empty = {
    moved: false,
    slidesBySection: {},
    touchedSectionIds: [],
    renamedIds: [],
    emptiedSectionIds: [],
    movedSlideIds: []
  };

  const source = slidesBySection || {};
  if (!toSectionId || !Array.isArray(source[toSectionId])) return empty;

  const order = sectionOrder.length ? sectionOrder : Object.keys(source);
  const rank = new Map(order.map((sectionId, index) => [sectionId, index]));

  // Resolve each ref to a real slide. By id first — a position is only ever a
  // fallback, because a position means nothing once anything else has moved.
  const moving = [];
  const movingSet = new Set();
  (Array.isArray(refs) ? refs : []).forEach((ref) => {
    if (!ref || !ref.sectionId) return;
    const sectionSlides = source[ref.sectionId];
    if (!Array.isArray(sectionSlides)) return;
    let index = ref.slideId
      ? sectionSlides.findIndex((slide) => slide && slide.slideId === ref.slideId)
      : -1;
    if (index < 0 && Number.isFinite(ref.slideIndex)) index = ref.slideIndex;
    const slide = index >= 0 ? sectionSlides[index] : null;
    if (!slide || movingSet.has(slide)) return;
    movingSet.add(slide);
    moving.push({ fromSectionId: ref.sectionId, index, slide });
  });
  if (!moving.length) return empty;

  moving.sort((a, b) => (
    ((rank.get(a.fromSectionId) ?? 0) - (rank.get(b.fromSectionId) ?? 0)) || (a.index - b.index)
  ));

  const isMoving = (slide) => movingSet.has(slide);
  const destinationBefore = source[toSectionId] || [];
  const insertAt = insertionIndexFor(destinationBefore, { toIndex, position, isMoving });

  const touched = new Set([toSectionId]);
  moving.forEach(({ fromSectionId }) => touched.add(fromSectionId));

  const next = {};
  touched.forEach((sectionId) => {
    next[sectionId] = (source[sectionId] || []).filter((slide) => !isMoving(slide));
  });

  // A section showing nothing but a blank placeholder is showing nothing. Drop
  // it rather than leaving an empty slide above whatever arrives.
  const destinationAfter = isEmptyPlaceholder(next[toSectionId]) ? [] : next[toSectionId];

  const renamedIds = [];
  const movedSlideIds = [];
  const prepared = moving.map(({ fromSectionId, slide }) => {
    const previousSlideId = slide.slideId || '';
    const slideId = fromSectionId === toSectionId
      ? (previousSlideId || mintSlideId(toSectionId))
      : mintSlideId(toSectionId);
    if (previousSlideId && previousSlideId !== slideId) renamedIds.push([previousSlideId, slideId]);
    movedSlideIds.push(slideId);
    const blocks = (Array.isArray(slide.blocks) ? slide.blocks : []).map((block) => (
      block && block.slideId === slideId ? block : { ...block, slideId }
    ));
    return { ...slide, slideId, blocks };
  });

  const landed = [...destinationAfter];
  landed.splice(Math.min(insertAt, landed.length), 0, ...prepared);
  next[toSectionId] = landed;

  // A section the move drained keeps a blank, so the deck never holds a
  // section with no slide at all.
  const emptiedSectionIds = [];
  Object.keys(next).forEach((sectionId) => {
    if (next[sectionId].length) return;
    emptiedSectionIds.push(sectionId);
    next[sectionId] = [{ slideId: mintSlideId(sectionId), blocks: [] }];
  });

  return {
    moved: true,
    slidesBySection: next,
    touchedSectionIds: Array.from(touched),
    renamedIds,
    emptiedSectionIds,
    movedSlideIds
  };
}
